import { useState } from 'react'
import { useCrosswordStore } from '../store'
import { getWordsInGroup, getLetters, getLettersFromText } from '../lib/pairings'
import { useSearch } from '../contexts/SearchContext'
import { usePlacedWordTexts, isWordPlaced } from '../hooks/usePlacedWordTexts'
import { TabGroup } from './TabGroup'
import { WordListItem } from './ListItems'
import { CollapsibleSection } from './CollapsibleSection'
import { SearchFilterBar } from './SearchFilterBar'
import { AssistantPanel } from './AssistantPanel'
import { WordAssistantBody } from './WordAssistantBody'
import type { ListSide, Word } from '../types'

const WORD_VIEW_MODES = ['byList', 'byLength'] as const
type WordViewMode = typeof WORD_VIEW_MODES[number]

/**
 * Top-level Words page. Lists every word across both lists with by-list and
 * by-length views, search, and group/twin badges. Adding new words happens
 * inline at the top of the page (no modal). When a word is selected, the
 * right-side AssistantPanel shows its group + variant suggestions.
 */
export function WordsView() {
  const { words, addWords, selectedWordId, setSelectedWord, setSelectedPairing, listNames } = useCrosswordStore()
  const searchContext = useSearch()
  const letterSearch = searchContext?.letterSearch ?? []
  const searchPattern = searchContext?.searchPattern ?? ''
  const placedWordTexts = usePlacedWordTexts()

  const [wordViewMode, setWordViewMode] = useState<WordViewMode>('byList')
  const [collapsedLists, setCollapsedLists] = useState<{ A: boolean; B: boolean }>({ A: false, B: false })
  const [collapsedLengths, setCollapsedLengths] = useState<Record<number, boolean>>({})

  // Inline bulk-add inputs — comma- or newline-separated entries get added to
  // the chosen list at once.
  const [addAText, setAddAText] = useState('')
  const [addBText, setAddBText] = useState('')
  const [assistantOpen, setAssistantOpen] = useState(true)

  // Search match helpers — mirrors ExploreColumn's logic so behavior stays
  // consistent across the two surfaces.
  const hasSearch = searchContext?.hasSearch ?? letterSearch.some(l => l.trim())
  const upperLetterSearch = letterSearch.map(l => l.toUpperCase())
  const filledSearchIndices = letterSearch.map((l, i) => l.trim() ? i : -1).filter(i => i >= 0)
  const searchLetters = letterSearch.filter(l => l.trim()).join('').toUpperCase()
  const isPrefixSearch = filledSearchIndices.every((idx, i) => idx === i)

  const matchesSearch = (text: string): boolean => {
    if (!hasSearch) return true
    const letters = getLettersFromText(text)
    if (isPrefixSearch) return letters.startsWith(searchLetters)
    return filledSearchIndices.every(idx => letters[idx] === upperLetterSearch[idx])
  }

  // Filter and sort words by length, then alphabetically.
  const baseWords = words.filter(w => w.list)
  const sortByLengthThenText = (a: Word, b: Word): number => {
    const lenA = getLetters(a).length
    const lenB = getLetters(b).length
    if (lenA !== lenB) return lenA - lenB
    return a.text.localeCompare(b.text)
  }
  const filteredWords: Word[] = hasSearch
    ? [...baseWords.filter(w => matchesSearch(getLetters(w)))].sort(sortByLengthThenText)
    : [...baseWords].sort(sortByLengthThenText)

  // Group by length for the by-length view.
  const wordGroupsByLen: Record<number, { A: Word[]; B: Word[] }> = {}
  for (const word of filteredWords) {
    const len = getLetters(word).length
    if (!wordGroupsByLen[len]) wordGroupsByLen[len] = { A: [], B: [] }
    if (word.list === 'A') wordGroupsByLen[len].A.push(word)
    else if (word.list === 'B') wordGroupsByLen[len].B.push(word)
  }
  const wordsByLength = Object.entries(wordGroupsByLen)
    .map(([len, ws]) => ({ length: parseInt(len), ...ws }))
    .sort((a, b) => a.length - b.length)

  const wordsA = filteredWords.filter(w => w.list === 'A')
  const wordsB = filteredWords.filter(w => w.list === 'B')

  // Variant counts: how many other words share the selected word's groupId.
  const variantCountByWordId = new Map<string, number>()
  const countByGroupId = new Map<string, number>()
  for (const word of words) {
    if (!word.groupId) continue
    countByGroupId.set(word.groupId, (countByGroupId.get(word.groupId) ?? 0) + 1)
  }
  for (const word of words) {
    if (!word.groupId) continue
    const groupCount = countByGroupId.get(word.groupId) ?? 0
    if (groupCount > 1) variantCountByWordId.set(word.id, groupCount - 1)
  }

  // Group highlight: when a word is selected, its variants are also highlighted.
  const selectedWord = selectedWordId ? words.find(w => w.id === selectedWordId) ?? null : null
  const groupWordIds = selectedWord ? new Set(getWordsInGroup(selectedWord, words).map(w => w.id)) : new Set<string>()

  const toggleListCollapsed = (list: 'A' | 'B') => {
    setCollapsedLists(prev => ({ ...prev, [list]: !prev[list] }))
  }

  const handleSelectWord = (word: Word) => {
    setSelectedWord(word.id)
    setSelectedPairing(null)
  }

  const submitAdd = (list: ListSide, text: string, setText: (s: string) => void) => {
    const trimmed = text.trim()
    if (!trimmed) return
    const wordTexts = trimmed.split(/[,\n]/).map(w => w.trim()).filter(Boolean)
    if (wordTexts.length > 0) addWords(wordTexts, list)
    setText('')
  }

  const handleAddKey = (e: React.KeyboardEvent, list: ListSide, text: string, setText: (s: string) => void) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submitAdd(list, text, setText)
    }
  }

  const totalWords = baseWords.length

  const renderWordRow = (word: Word, opts: { listLabel?: string } = {}) => (
    <div
      key={word.id}
      onClick={() => handleSelectWord(word)}
      className="cursor-pointer"
    >
      <WordListItem
        text={word.text}
        inverted={word.list === 'B'}
        autoHighlight
        selected={selectedWordId === word.id || groupWordIds.has(word.id)}
        isPlaced={isWordPlaced(placedWordTexts, word.text)}
        variantCount={variantCountByWordId.get(word.id)}
        letterCount={getLetters(word).length}
        {...(opts.listLabel ? { listLabel: opts.listLabel } : {})}
      />
    </div>
  )

  return (
    <div className="h-full flex gap-3">
      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-baseline gap-3">
            <h1 className="text-lg font-semibold text-gray-900">Words</h1>
            <span className="text-sm text-gray-400">
              {hasSearch ? `${filteredWords.length} of ${totalWords}` : totalWords}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <TabGroup
              tabs={WORD_VIEW_MODES}
              value={wordViewMode}
              onChange={setWordViewMode}
              labels={{ byList: 'By List', byLength: 'By Length' }}
              variant="underline"
              size="sm"
            />
            <button
              onClick={() => setAssistantOpen(o => !o)}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title={assistantOpen ? 'Close assistant' : 'Open assistant'}
            >
              {assistantOpen ? 'Hide assistant' : 'Show assistant'}
            </button>
          </div>
        </div>

        {/* Inline add — comma- or newline-separated entries split into multiple words. */}
        <div className="px-6 pb-3 shrink-0">
          <div className="flex gap-2 max-w-6xl">
            <div className="flex-1">
              <input
                type="text"
                value={addAText}
                onChange={e => setAddAText(e.target.value)}
                onKeyDown={e => handleAddKey(e, 'A', addAText, setAddAText)}
                placeholder={`Add to ${listNames.A} — comma or newline separated`}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                value={addBText}
                onChange={e => setAddBText(e.target.value)}
                onKeyDown={e => handleAddKey(e, 'B', addBText, setAddBText)}
                placeholder={`Add to ${listNames.B} — comma or newline separated`}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
          </div>
        </div>

        {hasSearch && (
          <div className="px-6 shrink-0">
            <SearchFilterBar pattern={searchPattern} />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {wordViewMode === 'byList' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-6xl">
              <div className="bg-white rounded-lg shadow-sm p-3">
                <CollapsibleSection
                  label={listNames.A}
                  count={wordsA.length}
                  collapsed={collapsedLists.A}
                  onToggle={() => toggleListCollapsed('A')}
                >
                  <div className="space-y-0.5">
                    {wordsA.map(word => renderWordRow(word))}
                    {wordsA.length === 0 && (
                      <p className="text-xs text-gray-400 px-2 py-3">No words yet.</p>
                    )}
                  </div>
                </CollapsibleSection>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-3">
                <CollapsibleSection
                  label={listNames.B}
                  count={wordsB.length}
                  collapsed={collapsedLists.B}
                  onToggle={() => toggleListCollapsed('B')}
                >
                  <div className="space-y-0.5">
                    {wordsB.map(word => renderWordRow(word))}
                    {wordsB.length === 0 && (
                      <p className="text-xs text-gray-400 px-2 py-3">No words yet.</p>
                    )}
                  </div>
                </CollapsibleSection>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-3 space-y-2 max-w-3xl">
              {wordsByLength.map(({ length, A, B }) => (
                <CollapsibleSection
                  key={length}
                  label={`${length} letters`}
                  count={A.length + B.length}
                  collapsed={collapsedLengths[length] ?? false}
                  onToggle={() => setCollapsedLengths(prev => ({ ...prev, [length]: !prev[length] }))}
                >
                  <div className="space-y-0.5">
                    {A.map(word => renderWordRow(word, { listLabel: listNames.A }))}
                    {A.length > 0 && B.length > 0 && <div className="h-1" />}
                    {B.map(word => renderWordRow(word, { listLabel: listNames.B }))}
                  </div>
                </CollapsibleSection>
              ))}
              {wordsByLength.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">
                  {hasSearch ? 'No words match the search.' : 'No words yet — type above and press Enter to add.'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right-side AssistantPanel */}
      <AssistantPanel
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        chromeless
        body={<WordAssistantBody onClose={() => setAssistantOpen(false)} />}
      />
    </div>
  )
}
